import React, { useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { isSuperAdminPortalView, isHrPortalView, isHrAdminPortalView, isHrOpsPortalView } from '../utils/hrAccess';
import { useTeamSize } from '../hooks/useTeamSize';
import { canRaiseTicket } from '../utils/ticketAccess';

/**
 * The bottom bar, on phones only.
 *
 * A hamburger menu is a web pattern: it hides everything behind a button and
 * costs two taps to reach anything. Phone apps put the few things people
 * actually do along the bottom, under the thumb, always visible — and that one
 * difference does more to make this feel like an app than any amount of
 * styling.
 *
 * Four destinations, no More. Everything else is behind the menu button in
 * the header, which appears at the same width — a second way into the same
 * menu earns its place only if the first is hard to find, and that one is in
 * the corner every app puts it.
 *
 * Four because a fifth starts to crowd at 360px, and because past four nobody
 * is choosing from a bar, they are searching.
 *
 * Which four depends on the person. An employee lives in Attendance and Leave;
 * HR lives in approvals; an admin lives in tickets. Showing a nurse's menu to
 * a cook helps neither.
 */

const Item = ({ to, icon, label, badge }) => (
  <NavLink
    to={to}
    className={({ isActive }) => `bnav-item${isActive ? ' active' : ''}`}
  >
    <span className="bnav-icon">
      <i className={`ti ${icon}`} />
      {badge > 0 && <span className="bnav-badge">{badge > 9 ? '9+' : badge}</span>}
    </span>
    <span className="bnav-label">{label}</span>
  </NavLink>
);

/*
 * Home, Directory, Referrals.
 *
 * Attendance and Tickets were here on the argument that a thumb reaches for
 * clocking in twice a day. In practice both are one tap away on the home
 * screen, and the bar is more useful carrying the things that are not —
 * which is what it now does.
 *
 * `hidden` carries the routes this person would be bounced from. Referrals is
 * one of them: the router sends HR Ops and the Super Admin back to the
 * dashboard, and a tab that returns you to where you came from reads as
 * broken. App.js works the list out once and passes it to the home screen and
 * to this.
 */
/*
 * The + in the middle of the bar: the four things people start from a phone,
 * one tap from anywhere. Each lands on its page with the form already open
 * (?apply=1, ?new=1), so it is the same form as on the page itself.
 */
const QUICK = [
  { key: 'leave', to: '/leave?apply=1', path: '/leave', icon: 'ti-beach', label: 'Apply leave', tone: '#e11d74' },
  { key: 'ticket', to: '/raise-ticket', path: '/raise-ticket', icon: 'ti-ticket', label: 'Raise ticket', tone: '#2563eb' },
  { key: 'expense', to: '/expenses?new=1', path: '/expenses', icon: 'ti-receipt', label: 'Claim expense', tone: '#15803d' },
  { key: 'task', to: '/my-tasks?new=1', path: '/my-tasks', icon: 'ti-checklist', label: 'Add task', tone: '#d97706' },
];

const BottomNav = ({ hidden = [] }) => {
  const { user, can } = useAuth();
  const teamSize = useTeamSize(user);
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  // Close the menu whenever the page changes.
  useEffect(() => { setOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  if (!user) return null;

  /*
   * The org chart is the one destination no permission covers: the API allows
   * admins, HR, and anyone who manages somebody. Same rule the sidebar and the
   * home screen apply, from one shared answer rather than three requests.
   */
  const canSeeOrgChart = user.role === 'admin'
    || isSuperAdminPortalView(user) || isHrPortalView(user)
    || isHrAdminPortalView(user) || isHrOpsPortalView(user)
    || teamSize > 0;

  const items = [];
  if (can('dashboard')) items.push({ to: '/dashboard', icon: 'ti-layout-dashboard', label: 'Home' });
  if (canSeeOrgChart) items.push({ to: '/org-chart', icon: 'ti-address-book', label: 'Directory' });
  if (can('referrals')) items.push({ to: '/referrals', icon: 'ti-user-plus', label: 'Referrals' });

  const shown = items.filter((i) => !hidden.includes(i.to)).slice(0, 4);

  const allowed = {
    leave: can('leave'),
    ticket: canRaiseTicket(user, can),
    expense: can('expenses'),
    task: can('my_tasks'),
  };
  const quick = QUICK.filter((q) => allowed[q.key] && !hidden.includes(q.path));

  if (!shown.length && !quick.length) return null;

  // The + sits in the middle: half the tabs either side of it.
  const half = Math.ceil(shown.length / 2);
  const fab = quick.length > 0 && (
    <div className="bnav-fab-slot" key="fab">
      <button
        type="button"
        className={`bnav-fab${open ? ' open' : ''}`}
        aria-label={open ? 'Close quick actions' : 'Quick actions'}
        aria-expanded={open}
        onClick={() => { if (navigator.vibrate) navigator.vibrate(8); setOpen((o) => !o); }}
      >
        <i className="ti ti-plus" />
      </button>
    </div>
  );

  return (
    <>
      {open && (
        <div className="qa-sheet-wrap" onClick={() => setOpen(false)} role="presentation">
          <div className="qa-sheet" role="menu" aria-label="Quick actions" onClick={(e) => e.stopPropagation()}>
            {quick.map((q, i) => (
              <button
                key={q.key}
                type="button"
                role="menuitem"
                className="qa-item"
                style={{ '--qc': q.tone, '--i': i }}
                onClick={() => { setOpen(false); navigate(q.to); }}
              >
                <span className="qa-ic"><i className={`ti ${q.icon}`} /></span>
                <span>{q.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      <nav className="bnav" aria-label="Main">
        {shown.slice(0, half).map((i) => <Item key={i.to} {...i} />)}
        {fab}
        {shown.slice(half).map((i) => <Item key={i.to} {...i} />)}
      </nav>
    </>
  );
};

export default BottomNav;
